import { Box, Button, IconButton, SxProps, Theme, useTheme } from '@mui/material';
import {
  ComponentPropsWithoutRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { HeroCarouselFooterText } from './HeroCarouselFooterText';
import { HXCIcon } from '@ui-kit/components/HXCIcon';
import { Text } from '@ui-kit/components/Text';
import { Z_INDEX } from '@ui-kit/theme';
import {
  CarouselDotPagination,
  useCarouselDotPagination,
} from '@ui-kit/components/CarouselDotPagination';
import {
  ARROW_BUTTON_WIDTH_SPACING,
  ARROW_GUTTER_MD_SPACING,
  AUTO_CYCLE_IN_MS,
  DEFAULT_CAROUSEL_HEIGHT,
  HOME_HERO_MOBILE_CAROUSEL_HEIGHT_PX,
  MOBILE_LOGO_SLOT_TOTAL_PX,
  DESKTOP_IMAGE_HEIGHT_PX,
  DESKTOP_IMAGE_MIN_WIDTH_PX,
  DESKTOP_TEXT_MAX_WIDTH_PX,
  GAP_AFTER_ARROW_SPACING,
  MOBILE_IMAGE_MAX_HEIGHT_PX,
  TABLET_IMAGE_HEIGHT_PX,
  TABLET_IMAGE_MIN_LEFT_PX,
  TABLET_IMAGE_WIDTH_PX,
  TABLET_TEXT_LEFT_PX,
  TABLET_TEXT_MAX_WIDTH_PX,
} from './consts';
import { HeroCarouselLogoAsset } from './HeroCarouselLogoAsset';

type ImageLike = string | { src: string };
export type HeroCarouselTextTheme = 'light' | 'dark';

export interface HeroSlide {
  id?: string | number;
  backgroundColor: string;
  textTheme?: HeroCarouselTextTheme;
  title?: string;
  bodyText?: string | ReactNode;
  ctaButton?: { label: string; href: string; isOpenInNewTab?: boolean };
  imageDesktopSrc: ImageLike;
  imageMobileSrc?: ImageLike;
  imageAlt: string;
  logoAssetSrc?: ImageLike;
  logoAssetAlt?: string;
  logoAssetCssClass?: string;
  logoAssetInlineStyle?: string;
  footerText?: string;
  footerCssClass?: string;
}

export interface HeroCarouselProps extends ComponentPropsWithoutRef<'section'> {
  slides: HeroSlide[];
  desktopButtonsGutterRem: number;
  height?: { desktop: number; tablet: number; mobile: number };
  reserveMobileLogoSpace?: boolean;
  prioritySlideIndex?: number;
  sx?: SxProps<Theme>;
  linkComponent?: React.ElementType;
}

export const HeroCarousel = ({
  slides,
  desktopButtonsGutterRem = 12.5,
  height = DEFAULT_CAROUSEL_HEIGHT,
  reserveMobileLogoSpace = false,
  prioritySlideIndex,
  className,
  sx,
  linkComponent,
  ...sectionProps
}: HeroCarouselProps) => {
  const theme = useTheme();

  const carouselHeight = useMemo(
    () => ({
      ...height,
      mobile: reserveMobileLogoSpace ? HOME_HERO_MOBILE_CAROUSEL_HEIGHT_PX : height.mobile,
    }),
    [height, reserveMobileLogoSpace],
  );

  const autoplayPlugin = useMemo(
    () =>
      Autoplay({
        delay: AUTO_CYCLE_IN_MS,
        stopOnInteraction: true,
        stopOnMouseEnter: true,
      }),
    [],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: slides.length > 1, align: 'start' },
    slides.length > 1 ? [autoplayPlugin] : [],
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const { selectedIndex, scrollSnaps, onDotClick } = useCarouselDotPagination(emblaApi);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setActiveIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  const handleNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const handlePrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);

  const toImageUrl = (image: ImageLike | undefined): string =>
    typeof image === 'string' ? image : (image?.src ?? '');

  const textColorFor = (textTheme: HeroCarouselTextTheme | undefined) =>
    textTheme === 'light' ? theme.palette.common.white : theme.palette.text.primary;

  const responsiveHeight = {
    xs: carouselHeight.mobile,
    sm: carouselHeight.tablet,
    md: carouselHeight.desktop,
  };

  const paginationVariant = useMemo(() => {
    const activeSlide = slides[Math.max(0, Math.min(slides.length - 1, activeIndex))];
    return activeSlide?.textTheme === 'light' ? ('white' as const) : ('black' as const);
  }, [activeIndex, slides]);

  return (
    <Box
      className={className}
      sx={{
        position: 'relative',
        ...sx,
      }}
      {...sectionProps}
    >
      {/* Desktop navigation arrows */}
      {slides.length > 1 && (
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            inset: 0,
            zIndex: (t) => Z_INDEX.layers.controls(t),
            pointerEvents: 'none',
          }}
        >
          <Box
            className="hero-carousel-button-box"
            sx={{
              height: '100%',
              width: '100%',
              margin: '0 auto',
              position: 'relative',
            }}
          >
            <IconButton
              onClick={handlePrev}
              aria-label="previous"
              sx={(t) => ({
                position: 'absolute',
                top: '50%',
                left: {
                  md: t.spacing(ARROW_GUTTER_MD_SPACING),
                  lg: t.spacing(desktopButtonsGutterRem),
                },
                transform: 'translateY(-50%)',
                backgroundColor: t.palette?.white?.main || '#FFFFFF',
                '&:hover': {
                  backgroundColor: t.palette?.white?.main || '#FFFFFF',
                  filter: 'brightness(0.8)',
                },
                pointerEvents: 'auto',
              })}
            >
              <HXCIcon name="arrowLeftRg" />
            </IconButton>
            <IconButton
              onClick={handleNext}
              aria-label="next"
              sx={(t) => ({
                position: 'absolute',
                top: '50%',
                right: {
                  md: t.spacing(ARROW_GUTTER_MD_SPACING),
                  lg: t.spacing(desktopButtonsGutterRem),
                },
                transform: 'translateY(-50%)',
                backgroundColor: t.palette.white.main,
                '&:hover': { backgroundColor: t.palette.white.main, filter: 'brightness(0.8)' },
                pointerEvents: 'auto',
              })}
            >
              <HXCIcon name="arrowRightRg" />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Embla Carousel */}
      <Box sx={{ width: '100%', height: responsiveHeight }}>
        <Box ref={emblaRef} sx={{ overflow: 'hidden', width: '100%', height: '100%' }}>
          <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
            {slides.map((slide, slideIndex) => {
              const buttonColor = slide.textTheme === 'light' ? 'white' : 'black';
              const isPrioritySlide = slideIndex === prioritySlideIndex;

              const desktopSrc = toImageUrl(slide.imageDesktopSrc);
              const mobileSrc = toImageUrl(slide.imageMobileSrc);
              const fallbackSrc = mobileSrc || desktopSrc;
              const logoAssetSrc = toImageUrl(slide.logoAssetSrc);
              const showPlaceholder = !fallbackSrc;

              const imageBoxSx = {
                width: {
                  xs: '100%',
                  sm: `${TABLET_IMAGE_WIDTH_PX}px`,
                  md: '100%',
                },
                height: {
                  xs: '100%',
                  sm: `${TABLET_IMAGE_HEIGHT_PX}px`,
                  md: `${DESKTOP_IMAGE_HEIGHT_PX}px`,
                },
                maxHeight: { xs: `${MOBILE_IMAGE_MAX_HEIGHT_PX}px`, sm: 'none', md: '100%' },
                objectFit: { xs: 'cover', sm: 'cover', md: 'cover' },
                objectPosition: { xs: 'top center', sm: 'center', md: 'left center' },
                minWidth: {
                  xs: undefined,
                  sm: `${TABLET_IMAGE_WIDTH_PX}px`,
                  md: `${DESKTOP_IMAGE_MIN_WIDTH_PX}px`,
                },
                display: 'block',
                flexShrink: 0,
              } as const;

              return (
                <Box
                  key={slide.id ?? slide.imageAlt}
                  aria-roledescription="slide"
                  sx={{ flex: '0 0 100%', minWidth: 0 }}
                >
                  <Box
                    sx={{
                      height: responsiveHeight,
                      minHeight: responsiveHeight,
                      width: '100%',
                      backgroundColor: slide.backgroundColor,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      className="hero-carousel-grid"
                      sx={{
                        height: '100%',
                        width: '100%',
                        margin: '0 auto',
                        px: { xs: 2, sm: 0, md: 0 },
                        display: { xs: 'grid', sm: 'block', md: 'grid' },
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        alignItems: { xs: 'start', md: 'center' },
                        justifyItems: { xs: 'center', md: 'flex-start' },
                        position: 'relative',
                        overflow: { xs: 'visible', sm: 'hidden', md: 'visible' },
                        pb: { xs: 4, sm: 0, md: 0 },
                        pt: { xs: 3, sm: 0, md: 0 },
                      }}
                    >
                      {/* Text content */}
                      <Box
                        className="hero-carousel-textbox"
                        textAlign={{ xs: 'center', sm: 'left', md: 'left' }}
                        sx={(t) => {
                          const textLeftMarginMd =
                            ARROW_GUTTER_MD_SPACING +
                            ARROW_BUTTON_WIDTH_SPACING +
                            GAP_AFTER_ARROW_SPACING;
                          const textLeftMarginLg =
                            desktopButtonsGutterRem +
                            ARROW_BUTTON_WIDTH_SPACING +
                            GAP_AFTER_ARROW_SPACING * 1.5;
                          const textLeftMarginXl =
                            desktopButtonsGutterRem +
                            ARROW_BUTTON_WIDTH_SPACING +
                            GAP_AFTER_ARROW_SPACING * 2;

                          return {
                            position: { xs: 'relative', sm: 'absolute', md: 'relative' },
                            top: { sm: '50%', md: 'auto' },
                            left: { sm: `${TABLET_TEXT_LEFT_PX}px`, md: 'auto' },
                            transform: { sm: 'translateY(-50%)', md: 'none' },
                            zIndex: Z_INDEX.layers.controls(t),
                            justifySelf: { xs: 'center', md: 'start' },
                            ml: {
                              xs: 0,
                              sm: 0,
                              md: t.spacing(textLeftMarginMd),
                              lg: t.spacing(textLeftMarginLg),
                              xl: t.spacing(textLeftMarginXl),
                            },
                            mr: { xs: 0, md: 4 },
                            maxWidth: {
                              xs: 'none',
                              sm: `${TABLET_TEXT_MAX_WIDTH_PX}px`,
                              md: `${DESKTOP_TEXT_MAX_WIDTH_PX}px`,
                            },
                            width: { xs: '100%', sm: 'auto', md: '100%' },
                          };
                        }}
                      >
                        <HeroCarouselLogoAsset
                          src={logoAssetSrc || undefined}
                          alt={slide.logoAssetAlt}
                          cssClass={slide.logoAssetCssClass}
                          inlineStyle={slide.logoAssetInlineStyle}
                          reserveMobileLogoSpace={reserveMobileLogoSpace}
                        />
                        {slide.title && (
                          <Text
                            variant="h2"
                            mb={{ xs: '0.75rem', md: 3 }}
                            sx={{ color: textColorFor(slide.textTheme) }}
                          >
                            {slide.title}
                          </Text>
                        )}
                        {slide.bodyText && (
                          <Text
                            variant="p"
                            mb={{ ...(slide.ctaButton ? { xs: 2, md: 4 } : 0) }}
                            sx={{ color: textColorFor(slide.textTheme) }}
                          >
                            {slide.bodyText}
                          </Text>
                        )}
                        {slide.ctaButton && (
                          <Button
                            component={linkComponent ?? 'a'}
                            target={slide.ctaButton.isOpenInNewTab ? '_blank' : undefined}
                            rel={slide.ctaButton.isOpenInNewTab ? 'noopener noreferrer' : undefined}
                            variant="contained"
                            color={buttonColor}
                            {...(linkComponent
                              ? { to: slide.ctaButton.href }
                              : { href: slide.ctaButton.href })}
                          >
                            {slide.ctaButton.label}
                          </Button>
                        )}
                        {slide.footerText && (
                          <HeroCarouselFooterText
                            footerText={slide.footerText}
                            className={slide.footerCssClass}
                            color={textColorFor(slide.textTheme)}
                            marginTop={slide.ctaButton ? 2 : { xs: 2, md: 3 }}
                          />
                        )}
                        {reserveMobileLogoSpace && !logoAssetSrc && (
                          <Box
                            aria-hidden
                            sx={{
                              display: { xs: 'block', sm: 'none' },
                              width: '100%',
                              height: `${MOBILE_LOGO_SLOT_TOTAL_PX}px`,
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Box>

                      {/* Image container */}
                      <Box
                        sx={{
                          position: { xs: 'absolute', sm: 'absolute', md: 'relative' },
                          top: { xs: 'auto', sm: 0, md: 'auto' },
                          bottom: { xs: 0, sm: 0, md: 'auto' },
                          left: {
                            xs: 0,
                            sm: `max(${TABLET_IMAGE_MIN_LEFT_PX}px, calc(100vw - ${TABLET_IMAGE_WIDTH_PX}px))`,
                            md: 'auto',
                          },
                          right: { xs: 0, sm: 'auto', md: 'auto' },
                          height: {
                            xs: `${MOBILE_IMAGE_MAX_HEIGHT_PX}px`,
                            sm: '100%',
                            md: '100%',
                          },
                          width: { xs: '100%', sm: `${TABLET_IMAGE_WIDTH_PX}px`, md: '100%' },
                          display: 'flex',
                          alignItems: { xs: 'flex-start', sm: 'center', md: 'center' },
                          justifyContent: { xs: 'center', sm: 'flex-start', md: 'flex-start' },
                          overflow: { xs: 'hidden', sm: 'visible', md: 'hidden' },
                          flex: { xs: '0 0 auto', md: '1 1 auto' },
                          minWidth: 'auto',
                        }}
                      >
                        {showPlaceholder ? (
                          <Box
                            component="div"
                            role="img"
                            aria-label="placeholder"
                            sx={{
                              ...imageBoxSx,
                              backgroundColor: (t) => t.palette.grey[300],
                              width: { xs: '100%', md: `${DESKTOP_IMAGE_HEIGHT_PX}px` },
                              height: {
                                xs: `${MOBILE_IMAGE_MAX_HEIGHT_PX}px`,
                                md: `${DESKTOP_IMAGE_HEIGHT_PX}px`,
                              },
                            }}
                          />
                        ) : (
                          <Box
                            component="picture"
                            sx={{
                              ...imageBoxSx,
                              overflow: 'hidden',
                              border: 0,
                              lineHeight: 0,
                            }}
                          >
                            {desktopSrc ? (
                              <source
                                media={`(min-width: ${theme.breakpoints.values.sm}px)`}
                                srcSet={desktopSrc}
                              />
                            ) : null}
                            <Box
                              component="img"
                              src={fallbackSrc}
                              alt={slide.imageAlt}
                              loading={isPrioritySlide ? 'eager' : 'lazy'}
                              fetchPriority={isPrioritySlide ? 'high' : 'auto'}
                              width={DESKTOP_IMAGE_MIN_WIDTH_PX}
                              height={DESKTOP_IMAGE_HEIGHT_PX}
                              sx={{
                                display: 'block',
                                width: '100%',
                                height: '100%',
                                objectFit: imageBoxSx.objectFit,
                                objectPosition: imageBoxSx.objectPosition,
                                border: 0,
                                outline: 0,
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Dot pagination */}
        {slides.length > 1 && (
          <CarouselDotPagination
            scrollSnaps={scrollSnaps}
            selectedIndex={selectedIndex}
            onDotClick={onDotClick}
            variant={paginationVariant}
            sx={{
              position: 'absolute',
              bottom: '1rem',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: (t) => Z_INDEX.layers.controls(t),
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default HeroCarousel;
